// client/src/components/admin/project/ApplicationsViewModal.js
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

export default function ApplicationsViewModal({ open, onClose, projectId, projectNumber }) {
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/projects/${projectId}/applications`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setApplications(data.applications || []);
        setProjectInfo(data.project || null);
      } else {
        alert('지원자 목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('지원자 목록 조회 오류:', error);
      alert('지원자 목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && projectId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  if (!open) return null;

  // 통계 계산
  const statistics = {
    total: applications.length,
    accepted: applications.filter(a => a.contractStatus === 'accepted').length,
    pending: applications.filter(a => a.contractStatus === 'pending').length,
    selected: applications.filter(a => a.status === 'selected' && a.contractStatus === 'none').length,
    applicationOnly: applications.filter(a => a.status === 'pending').length
  };

  // SNS 아이콘
  const getSNSIcon = (platform) => {
    const icons = {
      instagram: '📷',
      youtube: '📺',
      blog: '📝',
      tiktok: '🎵'
    };
    return icons[platform] || '📱';
  };

  // 팔로워 수 포맷
  const formatFollowers = (count) => {
    if (count >= 10000) {
      return `${Math.floor(count / 10000)}만`;
    }
    return count?.toLocaleString() || '-';
  };

  // 상태 텍스트 및 스타일 클래스
  const getStatusInfo = (app) => {
    if (app.contractStatus === 'accepted') {
      return { text: '✅ 계약 완료', className: 'applications-view-modal__status--accepted' };
    }
    if (app.contractStatus === 'rejected') {
      return { text: '❌ 계약 거절', className: 'applications-view-modal__status--rejected' };
    }
    if (app.contractStatus === 'pending') {
      return { text: '⏳ 수락 대기 중', className: 'applications-view-modal__status--pending' };
    }
    if (app.contractStatus === 'expired') {
      return { text: '⏰ 계약 만료', className: 'applications-view-modal__status--expired' };
    }
    if (app.status === 'selected') {
      return { text: '📨 선정됨 (계약요청 전송)', className: 'applications-view-modal__status--selected' };
    }
    if (app.status === 'pending') {
      return { text: '⏸️ 미선정', className: 'applications-view-modal__status--unselected' };
    }
    if (app.status === 'expired') {
      return { text: '⏰ 선정 만료', className: 'applications-view-modal__status--expired' };
    }
    return { text: '-', className: '' };
  };

  return (
    <div className="applications-view-modal">
      <div className="applications-view-modal__content">
        {/* 헤더 */}
        <div className="applications-view-modal__header">
          <div>
            <h2 className="applications-view-modal__title">
              인플루언서 선정 계약 상세
            </h2>
            <p className="applications-view-modal__subtitle">
              Project NO.{projectNumber || 'N/A'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="applications-view-modal__close"
          >
            ×
          </button>
        </div>

        {/* 통계 영역 */}
        <div className="applications-view-modal__stats">
          <div className="applications-view-modal__stat">
            <span className="applications-view-modal__stat-label">계약 완료</span>
            <span className="applications-view-modal__stat-value applications-view-modal__stat-value--success">
              {statistics.accepted}명
            </span>
          </div>
          <div className="applications-view-modal__stat">
            <span className="applications-view-modal__stat-label">수락 대기</span>
            <span className="applications-view-modal__stat-value applications-view-modal__stat-value--warning">
              {statistics.pending}명
            </span>
          </div>
          <div className="applications-view-modal__stat">
            <span className="applications-view-modal__stat-label">선정됨</span>
            <span className="applications-view-modal__stat-value applications-view-modal__stat-value--primary">
              {statistics.selected}명
            </span>
          </div>
          <div className="applications-view-modal__stat">
            <span className="applications-view-modal__stat-label">모집 인원</span>
            <span className="applications-view-modal__stat-value">
              {projectInfo?.recruit?.headcount || 0}명
            </span>
          </div>
          <div className="applications-view-modal__stat">
            <span className="applications-view-modal__stat-label">총 지원</span>
            <span className="applications-view-modal__stat-value">
              {statistics.total}명
            </span>
          </div>
        </div>

        {/* 바디 */}
        <div className="applications-view-modal__body">
          {loading ? (
            <div className="applications-view-modal__loading">
              로딩 중...
            </div>
          ) : applications.length === 0 ? (
            <div className="applications-view-modal__empty">
              지원자가 없습니다.
            </div>
          ) : (
            <table className="applications-view-modal__table">
              <thead>
                <tr>
                  <th>NO</th>
                  <th>인플루언서</th>
                  <th>만족도 점수</th>
                  <th>지원한 SNS 채널</th>
                  <th>취소 횟수</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app, idx) => {
                  const statusInfo = getStatusInfo(app);
                  return (
                    <tr key={app.id}>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="applications-view-modal__influencer">
                          <div className="applications-view-modal__influencer-id">
                            {app.influencer?.name || '-'}
                          </div>
                          <div className="applications-view-modal__influencer-name">
                            {app.influencer?.userId || ''}
                          </div>
                        </div>
                      </td>
                      <td>
                        {app.influencer?.satisfactionScore > 0 ? (
                          <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: 14 }}>
                            ⭐ {app.influencer.satisfactionScore.toFixed(1)}점 / 5점만점
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>아직 평가 없음</span>
                        )}
                      </td>
                      <td>
                        {app.appliedSNS && app.appliedSNS.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {app.appliedSNS.map((sns, snsIdx) => (
                              <a
                                key={snsIdx}
                                href={sns.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="applications-view-modal__sns"
                              >
                                <span className="applications-view-modal__sns-icon">
                                  {getSNSIcon(sns.platform)}
                                </span>
                                <span className="applications-view-modal__sns-info">
                                  <span className="applications-view-modal__sns-platform">
                                    {sns.platform}
                                  </span>
                                  <span className="applications-view-modal__sns-followers">
                                    {sns.accountName || '-'} ({formatFollowers(sns.followerCount)})
                                  </span>
                                </span>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>-</span>
                        )}
                      </td>
                      <td>
                        {app.influencer?.contractCancelCount || 0}회
                      </td>
                      <td>
                        <span className={`applications-view-modal__status ${statusInfo.className}`}>
                          {statusInfo.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 푸터 */}
        <div className="applications-view-modal__footer">
          <button
            onClick={onClose}
            className="applications-view-modal__button"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

